import React, { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../context/AuthContext';
import { useDepositSettings } from './useDepositSettings';
import GradientButton from '../../buttons/GradientButton';
import '../pages/payment-subsettings/PaymentSubsettings.css';

const AlertIconWarning = () => (
  <svg className="ps-alert__icon" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden>
    <path
      fill="currentColor"
      d="M12 3L1 21h22L12 3zm0 3.47L18.53 19H5.47L12 6.47zM11 10h2v4h-2v-4zm0 6h2v2h-2v-2z"
    />
  </svg>
);

const formatGlobalDepositSummary = (globalSettings) => {
  const type = globalSettings?.defaultSettings?.calculationType;
  const amount = globalSettings?.defaultSettings?.amount ?? 0;
  if (type === 'duration') return `$${amount} per hour`;
  if (type === 'percentage') return `${amount}% of service price`;
  if (type === 'fixed') return `$${amount} fixed deposit`;
  return '—';
};

const ServiceDepositSettings = ({ service, onSave }) => {
  const { companyData } = useAuth();
  const { settings: globalSettings, calculateServiceDeposit } = useDepositSettings(companyData);
  const [saving, setSaving] = useState(false);
  const [depositSettings, setDepositSettings] = useState({
    isEnabled: false,
    calculationType: 'duration',
    amount: 0,
    minimumAmount: 0,
    maximumAmount: 0,
  });

  const globalEnabled = Boolean(globalSettings?.globalEnabled);

  useEffect(() => {
    const s = service?.depositSettings || {};
    setDepositSettings({
      isEnabled: s.isEnabled || false,
      calculationType: s.calculationType || 'duration',
      amount: s.amount ?? 0,
      minimumAmount: s.minimumAmount ?? 0,
      maximumAmount: s.maximumAmount ?? 0,
    });
  }, [service?.id, service?.depositSettings]);

  const amountLabel =
    depositSettings.calculationType === 'duration'
      ? 'Amount per hour'
      : depositSettings.calculationType === 'percentage'
        ? 'Percentage'
        : 'Fixed amount';

  const getCalculationExample = () => {
    const type = depositSettings.calculationType;
    const amount = depositSettings.amount || 0;
    const durationHours = Math.max(1, (Number(service?.duration) || 60) / 60);
    const price = Number(service?.price) || 100;

    switch (type) {
      case 'duration':
        return `For this service (${service?.duration || 60} min), the deposit would be about $${(amount * durationHours).toFixed(2)}`;
      case 'percentage':
        return `For this service ($${price.toFixed(2)}), the deposit would be $${((price * amount) / 100).toFixed(2)}`;
      case 'fixed':
        return `This service would require a $${amount} deposit`;
      default:
        return '';
    }
  };

  const previewDeposit = useMemo(() => {
    if (!service || !globalEnabled) return 0;
    return calculateServiceDeposit({
      ...service,
      depositSettings: { ...depositSettings, isEnabled: true },
    });
  }, [service, globalEnabled, depositSettings, calculateServiceDeposit]);

  const handleChange = (field, value) => {
    setDepositSettings((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await onSave?.(depositSettings);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="ps-root ps-root--embedded">
      {!globalEnabled ? (
        <div className="ps-alert ps-alert--warning" role="status">
          <AlertIconWarning />
          <div>
            Deposits are disabled company-wide. Enable them under{' '}
            <strong>Settings → Payment Settings → Deposits</strong> before you can override rules for this service.
          </div>
        </div>
      ) : (
        <div className="ps-panel">
          <p className="ps-panel__title">Company default</p>
          <p>
            This service uses your global deposit rule ({formatGlobalDepositSummary(globalSettings)}) until you turn on
            a service-specific override below.
          </p>
        </div>
      )}

      <div className="ps-option-with-help">
        <label className="ps-option-box" htmlFor="service-deposit-enabled">
          <input
            id="service-deposit-enabled"
            type="checkbox"
            className="ps-checkbox"
            checked={depositSettings.isEnabled}
            disabled={!globalEnabled}
            onChange={(e) => handleChange('isEnabled', e.target.checked)}
          />
          <span className="ps-option-box__body">
            <span className="ps-option-box__title">Use custom deposit for this service</span>
            <p className="ps-option-box__hint">
              Tick the box to override the company default with rules that apply only to this service.
            </p>
          </span>
        </label>
        <button
          type="button"
          className="ps-help-btn"
          title="When enabled, booking deposits for this service use the amounts below instead of your global default."
          aria-label="Help: custom deposit for this service"
        >
          ?
        </button>
      </div>

      {depositSettings.isEnabled && globalEnabled && (
        <>
          <p className="ps-muted" style={{ marginBottom: '1rem' }}>
            Set custom deposit requirements for this service. Limits are optional.
          </p>

          <h4 className="ps-section-head">
            Calculation
            <button
              type="button"
              className="ps-help-btn"
              title="Same methods as Payment Settings: duration, fixed amount, or percentage of price."
              aria-label="Help: calculation"
            >
              ?
            </button>
          </h4>

          <div className="ps-form-grid">
            <div className="ps-field">
              <label htmlFor="service-deposit-calc-type">Calculation method</label>
              <select
                id="service-deposit-calc-type"
                className="ps-select"
                value={depositSettings.calculationType}
                onChange={(e) => handleChange('calculationType', e.target.value)}
              >
                <option value="duration">Based on duration</option>
                <option value="fixed">Fixed amount</option>
                <option value="percentage">Percentage of price</option>
              </select>
            </div>
            <div className="ps-field">
              <label htmlFor="service-deposit-amount">{amountLabel}</label>
              {depositSettings.calculationType === 'percentage' ? (
                <div className="ps-input-wrap">
                  <input
                    id="service-deposit-amount"
                    type="number"
                    className="ps-input"
                    min={0}
                    step={0.01}
                    value={depositSettings.amount}
                    onChange={(e) => handleChange('amount', Number(e.target.value))}
                  />
                  <span className="ps-input-suffix">%</span>
                </div>
              ) : (
                <input
                  id="service-deposit-amount"
                  type="number"
                  className="ps-input"
                  min={0}
                  step={0.01}
                  value={depositSettings.amount}
                  onChange={(e) => handleChange('amount', Number(e.target.value))}
                />
              )}
            </div>
          </div>

          <div className="ps-example">
            <div className="ps-example__label">Example</div>
            <p className="ps-example__text">{getCalculationExample()}</p>
            {previewDeposit > 0 ? (
              <p className="ps-example__text" style={{ marginTop: '0.5rem' }}>
                Estimated deposit for this service: <strong>${previewDeposit.toFixed(2)}</strong>
              </p>
            ) : null}
          </div>

          <h4 className="ps-section-head">Deposit limits (optional)</h4>
          <div className="ps-form-grid ps-form-grid--tight">
            <div className="ps-field">
              <label htmlFor="service-deposit-min">Minimum deposit</label>
              <input
                id="service-deposit-min"
                type="number"
                className="ps-input"
                min={0}
                step={0.01}
                value={depositSettings.minimumAmount}
                onChange={(e) => handleChange('minimumAmount', Number(e.target.value))}
              />
              <p className="ps-field-hint">Lowest acceptable deposit</p>
            </div>
            <div className="ps-field">
              <label htmlFor="service-deposit-max">Maximum deposit</label>
              <input
                id="service-deposit-max"
                type="number"
                className="ps-input"
                min={0}
                step={0.01}
                value={depositSettings.maximumAmount}
                onChange={(e) => handleChange('maximumAmount', Number(e.target.value))}
              />
              <p className="ps-field-hint">Highest acceptable deposit</p>
            </div>
          </div>

          <div className="ps-actions ps-actions--flush">
            <GradientButton variant="primary" className="ps-save-btn" onClick={handleSave} disabled={saving}>
              {saving ? (
                <>
                  <span className="ps-btn-spinner" />
                  Saving…
                </>
              ) : (
                'Save changes'
              )}
            </GradientButton>
          </div>
        </>
      )}
    </div>
  );
};

export default ServiceDepositSettings;
